/**
 * One line of the rundown tree, per kind:
 *
 *   segment   ▸ name · parts · summed duration (/ budget) · hidden
 *   part      ▸ title · expected duration · AUTO / UNTIMED / FLOAT / INVALID · script mark
 *   piece       type tag · name · source layer · start + duration
 *   adlib       type tag · name · ADLIB (GLOBAL) · source layer · duration
 *
 * plus who else is on the row ("Anna is editing"), the focus/selection state,
 * a drop indicator while dragging, and an inline name editor (Enter commits,
 * Esc cancels, blur commits). Dense on purpose: an operator scans hundreds of
 * these. Presentational; `RundownTree` owns focus, keys and drag.
 */
import { ChevronDownIcon, ChevronRightIcon, FileTextIcon } from 'lucide-react';
import { type KeyboardEvent, useRef, useState } from 'react';
import type { DropPosition } from '../../lib/editor/drop.js';
import { formatDuration } from '../../lib/editor/duration.js';
import type { RowPresence } from '../../lib/editor/presence.js';
import type { AdlibView, FlatRow, PartView, PieceView, SegmentView } from '../../lib/editor/viewModel.js';
import { cn } from '../../lib/utils.js';
import { Badge } from '../ui/badge.js';
import { presenceClass } from './PresenceAvatars.js';

export type TreeRowProps = {
  readonly row: FlatRow;
  readonly focused: boolean;
  readonly selected: boolean;
  readonly editing: boolean;
  readonly presence: readonly RowPresence[];
  /** Where a drop would land relative to this row, while something is dragged over it. */
  readonly drop: { readonly position: DropPosition; readonly allowed: boolean } | null;
  readonly dragging: boolean;
  /** The part is on air or next in a production running this rundown (null: neither, or not a part). */
  readonly playout: 'onair' | 'next' | null;
  readonly onToggleCollapse: () => void;
  readonly onCommitName: (value: string) => void;
  readonly onCancelEdit: () => void;
};

const INDENT = ['pl-1', 'pl-6', 'pl-11'] as const;

function nameOf(row: FlatRow): string {
  return row.view.kind === 'part' ? row.view.title : row.view.name;
}

function Segment({ view }: { readonly view: SegmentView }) {
  const over = view.budgetDuration !== null && view.totalDuration > view.budgetDuration;
  return (
    <>
      <span className="text-xs text-muted-foreground">{view.partCount} parts</span>
      {view.hidden ? <Badge variant="offline">hidden</Badge> : null}
      <span className={cn('ml-auto font-mono text-xs tabular-nums', over ? 'text-warning' : 'text-muted-foreground')} data-testid="segment-duration">
        {formatDuration(view.totalDuration)}
        {view.budgetDuration === null ? '' : ` / ${formatDuration(view.budgetDuration)}`}
      </span>
    </>
  );
}

function Part({ view }: { readonly view: PartView }) {
  return (
    <>
      {view.hasScript ? <FileTextIcon className="size-3.5 shrink-0 text-muted-foreground" aria-label="has script" /> : null}
      {view.autoNext ? <Badge variant="next">auto</Badge> : null}
      {view.untimed ? <Badge variant="standby">untimed</Badge> : null}
      {view.floated ? <Badge variant="offline">float</Badge> : null}
      {view.invalid === null ? null : (
        <Badge variant="warning" title={view.invalid}>
          invalid
        </Badge>
      )}
      <span className="ml-auto font-mono text-xs tabular-nums text-muted-foreground" data-testid="part-duration">
        {formatDuration(view.expectedDuration)}
      </span>
    </>
  );
}

function Piece({ view }: { readonly view: PieceView }) {
  const start = view.start === 'now' ? 'now' : `+${formatDuration(view.start)}`;
  return (
    <>
      <span className="truncate text-xs text-muted-foreground">{view.sourceLayer === '' ? 'no layer' : view.sourceLayer}</span>
      <span className="ml-auto font-mono text-xs tabular-nums text-muted-foreground" data-testid="piece-timing">
        {start}
        {view.duration === null ? '' : ` · ${formatDuration(view.duration)}`}
      </span>
    </>
  );
}

function Adlib({ view }: { readonly view: AdlibView }) {
  return (
    <>
      <Badge variant="preview" data-testid="adlib-badge">
        {view.global ? 'global adlib' : 'adlib'}
      </Badge>
      <span className="truncate text-xs text-muted-foreground">{view.sourceLayer === '' ? 'no layer' : view.sourceLayer}</span>
      <span className="ml-auto font-mono text-xs tabular-nums text-muted-foreground" data-testid="adlib-duration">
        {view.expectedDuration === null ? '' : formatDuration(view.expectedDuration)}
      </span>
    </>
  );
}

function NameEditor({ initial, onCommit, onCancel }: { readonly initial: string; readonly onCommit: (v: string) => void; readonly onCancel: () => void }) {
  const [value, setValue] = useState(initial);
  // A ref, not state: committing moves focus, whose blur calls `finish` again
  // synchronously, before a state update could land (it committed twice, and
  // undo then reverted only the duplicate — the two-user e2e test).
  const done = useRef(false);
  const finish = (commit: boolean) => {
    if (done.current) return;
    done.current = true;
    if (commit && value !== initial) onCommit(value);
    else onCancel();
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      finish(true);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      finish(false);
    }
  };
  return (
    <input
      aria-label="Name"
      data-testid="inline-name"
      className="h-6 min-w-0 flex-1 rounded-sm border border-ring bg-background px-1 text-sm outline-none"
      value={value}
      maxLength={500}
      autoFocus
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={onKeyDown}
      onBlur={() => finish(true)}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function PresenceMarks({ presence }: { readonly presence: readonly RowPresence[] }) {
  if (presence.length === 0) return null;
  const editing = presence.find((p) => p.activity === 'editing');
  return (
    <span className="flex shrink-0 items-center gap-1" data-testid="row-presence">
      {editing === undefined ? null : (
        <span className="text-xs text-muted-foreground" data-testid="row-editing-note">
          {editing.displayName.split('@')[0]} is editing
        </span>
      )}
      {presence.map((p) => (
        <span
          key={`${p.userId}:${p.activity}:${p.field ?? ''}`}
          title={`${p.displayName} ${p.activity === 'editing' ? `is editing${p.field === null ? '' : ` ${p.field}`}` : 'is here'}`}
          className={cn('flex size-4 items-center justify-center rounded-full text-xs leading-none', presenceClass(p.colour))}
        >
          {p.initials.charAt(0)}
        </span>
      ))}
    </span>
  );
}

const DROP_LINE: Readonly<Record<DropPosition, string>> = {
  before: 'before:absolute before:inset-x-0 before:top-0 before:h-0.5',
  after: 'before:absolute before:inset-x-0 before:bottom-0 before:h-0.5',
  inside: 'ring-2 ring-inset',
};

function rowClass(props: TreeRowProps, editingPresence: boolean): string {
  const { row, selected, focused, dragging, drop } = props;
  return cn(
    'relative flex h-8 items-center gap-2 border-b pr-2 text-sm',
    INDENT[row.depth],
    row.view.kind === 'segment' && 'bg-muted font-semibold',
    selected && 'bg-accent text-accent-foreground',
    focused && 'outline-2 -outline-offset-2 outline-ring',
    dragging && 'opacity-40',
    row.lost && 'italic',
    drop !== null && DROP_LINE[drop.position],
    drop !== null && (drop.allowed ? 'before:bg-primary ring-primary' : 'before:bg-destructive ring-destructive'),
    editingPresence && !selected && 'bg-secondary',
  );
}

function Expander({ row, onToggle }: { readonly row: FlatRow; readonly onToggle: () => void }) {
  if (!row.hasChildren) return <span className="size-5 shrink-0" />;
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={row.collapsed ? 'Expand' : 'Collapse'}
      className="flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      {row.collapsed ? <ChevronRightIcon className="size-4" /> : <ChevronDownIcon className="size-4" />}
    </button>
  );
}

function Details({ row }: { readonly row: FlatRow }) {
  switch (row.view.kind) {
    case 'segment':
      return <Segment view={row.view} />;
    case 'part':
      return <Part view={row.view} />;
    case 'piece':
      return <Piece view={row.view} />;
    case 'adlib':
      return <Adlib view={row.view} />;
  }
}

export function TreeRow(props: TreeRowProps) {
  const { row, editing, presence, drop, onToggleCollapse, onCommitName, onCancelEdit } = props;
  const name = nameOf(row);
  return (
    <div
      className={rowClass(props, presence.some((p) => p.activity === 'editing'))}
      data-kind={row.view.kind}
      data-drop={drop === null ? undefined : `${drop.position}${drop.allowed ? '' : '-refused'}`}
    >
      <Expander row={row} onToggle={onToggleCollapse} />
      {row.view.kind === 'piece' || row.view.kind === 'adlib' ? (
        <Badge variant="outline" className="w-10 font-mono" data-testid="piece-type">
          {row.view.typeShort}
        </Badge>
      ) : null}
      {editing ? (
        <NameEditor initial={name} onCommit={onCommitName} onCancel={onCancelEdit} />
      ) : (
        <span className={cn('min-w-0 truncate', name === '' && 'text-muted-foreground')} data-testid="row-name">
          {name === '' ? `(untitled ${row.view.kind})` : name}
        </span>
      )}
      {props.playout === null ? null : (
        <Badge variant={props.playout} data-testid="row-playout">
          {props.playout === 'onair' ? 'on air' : 'next'}
        </Badge>
      )}
      <PresenceMarks presence={presence} />
      <Details row={row} />
    </div>
  );
}
